import { useState, useRef, useEffect } from 'react';

interface Message {
  id: number;
  sender: 'user' | 'provider';
  text: string;
  time: string;
}

interface ChatModalProps {
  providerName: string;
  providerImage: string;
  providerCategory: string;
  onClose: () => void;
}

const initialMessages: Message[] = [
  {
    id: 1,
    sender: 'provider',
    text: '¡Hola! Estoy disponible para atenderte. ¿En qué puedo ayudarte?',
    time: '10:30',
  },
  {
    id: 2,
    sender: 'user',
    text: 'Buenos días, me gustaría saber más sobre el servicio y los precios.',
    time: '10:32',
  },
  {
    id: 3,
    sender: 'provider',
    text: 'Claro, con gusto. ¿Qué tipo de trabajo necesitas realizar? Así te puedo dar un estimado más preciso.',
    time: '10:34',
  },
];

const ChatModal = ({ providerName, providerImage, providerCategory, onClose }: ChatModalProps) => {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!inputText.trim()) return;
    const now = new Date();
    const timeStr = `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;
    const newMsg: Message = {
      id: Date.now(),
      sender: 'user',
      text: inputText.trim(),
      time: timeStr,
    };
    setMessages(prev => [...prev, newMsg]);
    setInputText('');

    // Simulate provider reply
    setTimeout(() => {
      const reply: Message = {
        id: Date.now() + 1,
        sender: 'provider',
        text: 'Gracias por tu mensaje. Me pondré en contacto contigo pronto para coordinar los detalles.',
        time: `${now.getHours()}:${String(now.getMinutes() + 1).padStart(2, '0')}`,
      };
      setMessages(prev => [...prev, reply]);
    }, 1500);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-2xl w-full max-w-lg flex flex-col overflow-hidden" style={{ height: '580px' }}>
        {/* Header */}
        <div className="bg-orange-600 px-5 py-4 flex items-center gap-3">
          <div className="w-10 h-10 flex-shrink-0 rounded-full overflow-hidden border-2 border-white/30">
            <img src={providerImage} alt={providerName} className="w-full h-full object-cover object-top" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-base leading-tight truncate">{providerName}</h3>
            <p className="text-orange-100 text-xs truncate">{providerCategory}</p>
          </div>
          <div className="flex items-center gap-2 ml-2">
            <span className="flex items-center gap-1.5 text-xs text-orange-100">
              <span className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0 animate-pulse"></span>
              En línea
            </span>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition cursor-pointer text-white"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Monitoring notice */}
        <div className="bg-orange-50 border-b border-orange-100 px-4 py-2 flex items-start gap-2">
          <i className="ri-shield-user-line text-orange-500 text-sm flex-shrink-0 mt-0.5"></i>
          <p className="text-xs text-orange-700 leading-relaxed">
            Para garantizar la seguridad y el cumplimiento de nuestras normas, las conversaciones dentro de Task Zone pueden ser revisadas por nuestro equipo.
          </p>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.sender === 'provider' && (
                <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 mr-2 mt-1">
                  <img src={providerImage} alt={providerName} className="w-full h-full object-cover object-top" />
                </div>
              )}
              <div
                className={`max-w-[72%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.sender === 'user'
                    ? 'bg-orange-500 text-white rounded-br-md'
                    : 'bg-white text-gray-800 rounded-bl-md border border-gray-100'
                }`}
              >
                <p>{msg.text}</p>
                <p className={`text-xs mt-1 text-right ${msg.sender === 'user' ? 'text-orange-100' : 'text-gray-400'}`}>
                  {msg.time}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="px-4 py-3 bg-white border-t border-gray-100">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe un mensaje..."
              className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-orange-400 focus:bg-white transition"
            />
            <button
              onClick={handleSend}
              disabled={!inputText.trim()}
              className="w-10 h-10 flex items-center justify-center bg-orange-500 hover:bg-orange-600 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-xl transition cursor-pointer flex-shrink-0"
            >
              <i className="ri-send-plane-fill text-base"></i>
            </button>
          </div>
          <p className="text-xs text-gray-400 text-center mt-2">
            <i className="ri-lock-line mr-1"></i>
            Comunicación segura dentro de Task Zone
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;
